import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  School as SchoolIcon,
  Note as NoteIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowForwardIcon,
  CloseRounded,
} from '@mui/icons-material';
import { useAuth } from '../../../hooks/auth/useAuth';
import firestoreService from '../../../services/firestoreService';
import useLecutre from '../../../hooks/leacture-detail/useLecture';

function Note() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lectureList } = useLecutre();
  const [lectureMemos, setLectureMemos] = useState({});
  const [loading, setLoading] = useState(true);
  const [deletingMemoId, setDeletingMemoId] = useState(null);
  const [toast, setToast] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [query, setQuery] = useState('');

  const showToast = useCallback((message, type = 'info') => {
    setToast({ message, type });
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const fetchAllMemos = async () => {
    if (!user?.uid || !lectureList.length) {
      setLoading(false);
      return;
    }

    try {
      const memos = {};

      for (const lecture of lectureList) {
        const docPath = `LectureProgress/${lecture.id}/LectureMemo`;
        const result = await firestoreService.getCollection(docPath);

        if (result.success && result.data) {
          const userMemos = result.data.filter(
            (memo) => memo.id && memo.id.startsWith(`${user.uid}_`)
          );

          if (userMemos.length > 0) {
            memos[lecture.id] = {
              lectureName: lecture.name,
              memos: userMemos,
            };
          }
        }
      }

      setLectureMemos(memos);
    } catch (error) {
      console.error(error);
      showToast('메모를 불러오는 중 문제가 발생했습니다.', 'warn');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllMemos();
  }, [user?.uid, lectureList]);

  const requestDelete = (lectureId, memoId, e) => {
    e.stopPropagation();
    setDeleteTarget({ lectureId, memoId });
  };

  const cancelDelete = () => setDeleteTarget(null);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { lectureId, memoId } = deleteTarget;
    setDeleteTarget(null);
    setDeletingMemoId(memoId);

    try {
      const docPath = `LectureProgress/${lectureId}/LectureMemo`;
      const result = await firestoreService.deleteDocument(docPath, memoId);

      if (result.success) {
        await fetchAllMemos();
        showToast('메모를 삭제했습니다.');
      } else {
        showToast(result.error || '메모 삭제에 실패했습니다.', 'warn');
      }
    } catch (error) {
      showToast('메모 삭제 중 오류가 발생했습니다.', 'warn');
    } finally {
      setDeletingMemoId(null);
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingCard>
          <LoadingSpinner />
          <LoadingText>메모를 불러오는 중...</LoadingText>
        </LoadingCard>
      </Container>
    );
  }

  const hasMemos = Object.keys(lectureMemos).length > 0;
  const keyword = query.trim().toLowerCase();
  const visibleMemos = Object.entries(lectureMemos)
    .map(([lectureId, data]) => ({
      lectureId,
      data: {
        ...data,
        memos: data.memos.filter((memo) => {
          if (!keyword) return true;
          const haystack = `${data.lectureName || ''} ${memo.video_title || ''} ${memo.text || ''}`.toLowerCase();
          return haystack.includes(keyword);
        }),
      },
    }))
    .filter((entry) => entry.data.memos.length > 0);

  return (
    <Container>
      {toast && (
        <ToastBar $warn={toast.type === 'warn'} role="status">
          <span>{toast.message}</span>
          <ToastClose type="button" onClick={() => setToast(null)} aria-label="닫기">
            <CloseRounded style={{ fontSize: 20 }} />
          </ToastClose>
        </ToastBar>
      )}

      {deleteTarget && (
        <ConfirmBackdrop role="presentation" onClick={cancelDelete}>
          <ConfirmDialog role="dialog" aria-modal="true" aria-labelledby="memo-del-title" onClick={(e) => e.stopPropagation()}>
            <ConfirmTitle id="memo-del-title">메모 삭제</ConfirmTitle>
            <ConfirmText>이 메모를 삭제할까요? 삭제 후에는 복구할 수 없습니다.</ConfirmText>
            <ConfirmActions>
              <GhostBtn type="button" onClick={cancelDelete}>
                취소
              </GhostBtn>
              <DangerBtn type="button" onClick={confirmDelete}>
                삭제
              </DangerBtn>
            </ConfirmActions>
          </ConfirmDialog>
        </ConfirmBackdrop>
      )}

      <IntroBanner>
        <IntroIcon aria-hidden>
          <NoteIcon style={{ fontSize: 28 }} />
        </IntroIcon>
        <IntroCopy>
          <IntroTitle>강의 메모</IntroTitle>
          <IntroDesc>
            강의 단계에서 남긴 메모입니다. 카드를 누르면 그 문제의 강의로 돌아갑니다.
          </IntroDesc>
        </IntroCopy>
      </IntroBanner>

      {hasMemos && (
        <Search
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="강의 이름, 영상, 메모 내용 검색"
        />
      )}

      {hasMemos && visibleMemos.length === 0 && (
        <IntroDesc style={{ marginBottom: 16 }}>검색 결과가 없습니다.</IntroDesc>
      )}

      {hasMemos ? (
        <MemosGrid>
          {visibleMemos.map(({ lectureId, data }) => (
            <LectureMemoCard key={lectureId}>
              <CardHeader>
                <CardHeaderTop>
                  <LectureIcon>
                    <SchoolIcon />
                  </LectureIcon>
                  <GoLectureBtn
                    type="button"
                    onClick={() => navigate(`/lecture/${lectureId}`)}
                    aria-label={`${data.lectureName} 강의로 이동`}
                  >
                    강의 열기
                    <ArrowForwardIcon style={{ fontSize: 18 }} />
                  </GoLectureBtn>
                </CardHeaderTop>
                <LectureTitle>{data.lectureName}</LectureTitle>
                <MemoCount>{data.memos.length}개의 메모</MemoCount>
              </CardHeader>

              <MemoList>
                {data.memos.map((memo) => (
                  <MemoItem
                    key={memo.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/lecture/${lectureId}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/lecture/${lectureId}`);
                      }
                    }}
                    aria-label={`${memo.video_title || '메모'}, 강의로 이동`}
                  >
                    <MemoItemHeader>
                      <VideoTitle>{memo.video_title || '메모'}</VideoTitle>
                      <MemoItemActions>
                        <MemoDate>
                          {memo.updated_at?.toDate
                            ? new Date(memo.updated_at.toDate()).toLocaleString('ko-KR', {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              })
                            : '날짜 없음'}
                        </MemoDate>
                        <DeleteButton
                          type="button"
                          onClick={(e) => requestDelete(lectureId, memo.id, e)}
                          disabled={deletingMemoId === memo.id}
                          title="메모 삭제"
                          aria-label="메모 삭제"
                        >
                          {deletingMemoId === memo.id ? '…' : <DeleteIcon />}
                        </DeleteButton>
                      </MemoItemActions>
                    </MemoItemHeader>

                    {memo.text && (
                      <MemoTextPreview>
                        {memo.text.length > 120 ? `${memo.text.substring(0, 120)}…` : memo.text}
                      </MemoTextPreview>
                    )}

                    {memo.canvas_data && (
                      <CanvasPreview>
                        <CanvasImage src={memo.canvas_data} alt="" />
                      </CanvasPreview>
                    )}
                  </MemoItem>
                ))}
              </MemoList>
            </LectureMemoCard>
          ))}
        </MemosGrid>
      ) : (
        <EmptyState>
          <EmptyIcon>
            <NoteIcon />
          </EmptyIcon>
          <EmptyTitle>아직 작성한 메모가 없습니다</EmptyTitle>
          <EmptyText>강의 영상을 보며 메모를 남기면 이곳에 모입니다.</EmptyText>
          <EmptyActions>
            <PrimaryEmptyBtn type="button" onClick={() => navigate('/lecture')}>
              강의 목록으로
            </PrimaryEmptyBtn>
          </EmptyActions>
        </EmptyState>
      )}
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 24px 16px 40px;
  min-height: 70vh;
  position: relative;
`;

const ToastBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  margin-bottom: 16px;
  border: 1px solid ${(p) => (p.$warn ? '#fecaca' : '#e7e1d3')};
  background: ${(p) => (p.$warn ? '#fef2f2' : '#fffbea')};
  color: ${(p) => (p.$warn ? '#991b1b' : '#1d2340')};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
`;

const ToastClose = styled.button`
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  padding: 0;
  opacity: 0.75;
  display: flex;
  &:hover {
    opacity: 1;
  }
`;

const ConfirmBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ConfirmDialog = styled.div`
  width: 100%;
  max-width: 400px;
  background: #fff;
  border-radius: 16px;
  padding: 22px 22px 18px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.18);
`;

const ConfirmTitle = styled.h2`
  margin: 0 0 10px;
  font-size: 1.1rem;
  font-weight: 800;
  color: #0f172a;
`;

const ConfirmText = styled.p`
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 1.55;
  color: #475569;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const GhostBtn = styled.button`
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-weight: 700;
  font-size: 14px;
  color: #475569;
  cursor: pointer;
  &:hover {
    background: #f8fafc;
  }
`;

const DangerBtn = styled.button`
  padding: 10px 16px;
  border-radius: 10px;
  border: none;
  background: #dc2626;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    background: #b91c1c;
  }
`;

const Search = styled.input`
  width: 100%;
  height: 46px;
  margin-bottom: 16px;
  padding: 0 14px;
  font: inherit;
  color: #1d2340;
  background: #fff;
  border: 1px solid #e7e1d3;
  border-radius: 6px;

  &:focus {
    outline: none;
    border-color: #1d2340;
  }
`;

const IntroBanner = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 18px 20px;
  margin-bottom: 24px;
  border-radius: 16px;
  background: #fffbea;
  border: 1px solid rgba(231, 225, 211, 1);
`;

const IntroIcon = styled.div`
  color: #2c3561;
  display: flex;
  flex-shrink: 0;
`;

const IntroCopy = styled.div`
  min-width: 0;
`;

const IntroTitle = styled.h1`
  margin: 0 0 6px 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: #1d2340;
  letter-spacing: -0.02em;
`;

const IntroDesc = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.55;
  color: #1d2340;
`;

const MemosGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  width: 100%;
  @media (min-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const LectureMemoCard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 12px rgba(0, 0, 0, 0.04);
  background: white;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(29, 35, 64, 0.08);
    border-color: rgba(29, 35, 64, 0.16);
  }
`;

const CardHeader = styled.div`
  padding: 16px 18px;
  background: #1d2340;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  position: relative;
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 55%, rgba(0, 0, 0, 0.06) 100%);
    pointer-events: none;
  }
`;

const CardHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  position: relative;
  z-index: 1;
`;

const LectureIcon = styled.div`
  color: white;
  opacity: 0.95;
  .MuiSvgIcon-root {
    font-size: 32px;
  }
`;

const GoLectureBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const LectureTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: white;
  position: relative;
  z-index: 1;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MemoCount = styled.div`
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  position: relative;
  z-index: 1;
`;

const MemoList = styled.div`
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 400px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
`;

const MemoItem = styled.div`
  width: 100%;
  text-align: left;
  padding: 12px 12px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    background: rgba(29, 35, 64, 0.05);
    border-color: rgba(29, 35, 64, 0.16);
    box-shadow: 0 2px 8px rgba(29, 35, 64, 0.08);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(29, 35, 64, 0.18);
  }
`;

const MemoItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  gap: 8px;
`;

const MemoItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const VideoTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.35;
  min-width: 0;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MemoDate = styled.div`
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 500;
  white-space: nowrap;
`;

const MemoTextPreview = styled.div`
  font-size: 0.8125rem;
  color: #475569;
  line-height: 1.55;
  margin-bottom: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CanvasPreview = styled.div`
  width: 100%;
  height: 88px;
  border-radius: 8px;
  overflow: hidden;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CanvasImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  background: #fff;
  border-radius: 16px;
  border: 2px dashed #e2e8f0;
  background-color: #f8fafc;
`;

const EmptyIcon = styled.div`
  margin-bottom: 16px;
  color: #d9412b;
  .MuiSvgIcon-root {
    font-size: 56px;
  }
`;

const EmptyTitle = styled.h3`
  font-size: 1.15rem;
  margin: 0 0 8px 0;
  color: #334155;
  font-weight: 800;
`;

const EmptyText = styled.p`
  font-size: 0.9rem;
  margin: 0 0 20px;
  color: #64748b;
  line-height: 1.55;
`;

const EmptyActions = styled.div`
  display: flex;
  justify-content: center;
`;

const PrimaryEmptyBtn = styled.button`
  padding: 12px 22px;
  border-radius: 12px;
  border: none;
  background: #1d2340;
  color: #fff;
  font-weight: 800;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(79, 70, 229, 0.35);
  &:hover {
    filter: brightness(1.05);
  }
`;

const LoadingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 280px;
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #1d2340;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  font-size: 0.9375rem;
  color: #64748b;
  font-weight: 500;
`;

const DeleteButton = styled.button`
  background: transparent;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(239, 68, 68, 0.12);
    color: #dc2626;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  svg {
    font-size: 18px;
  }
`;

export default Note;
